/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  autocompleteValueFormatter,
  createDefaultGridModel,
  falsyCellRenderer,
  rendererIconByName,
  ROW_DELETED_CLASS,
  ROW_DIRTY_CLASS,
} from '@gh/common-internal-shared';
import { NumberTypes } from '@gh/common-shared';
import {
  buildEditableAutocompleteColumn,
  buildEditableNumberColumn,
  buildNumberColumn,
  createGridOptions,
  gridEditableValueCollectionComparator,
  gridValueCollectionComparator,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { valueComparator } from '@gh/core-util';
import { ColDef, GridOptions } from 'ag-grid-community';
import { assign } from 'lodash';
import { ComponentItem } from '../../resources/component-and-item-details.model';
import { ComponentDetailsBusinessService } from '../../resources/component-details-business.service';
import { ComponentDetailsFormState } from '../component-details-tab-container/component-details-form.service';
import { ComponentDetailsCommandService } from './component-details-command.service';
import {
  getComponentItemId,
  getComponentItemState,
  validateItemField,
} from './component-details-items.state';

@Injectable()
export class ComponentDetailsItemsGridDefinition extends LayoutBasedGridDefinition {
  /**
   * This model retrieves state of cells from the entity itself
   * and runs all side effects by triggering corresponding commands.
   */
  private model = createDefaultGridModel<ComponentItem>({
    getId: getComponentItemId,
    command: (entity, name, value) => this.commands.updateItemField(
      getComponentItemId(entity),
      name,
      value),
    validate: validateItemField,
    getCellDef: getComponentItemState,
  });

  constructor(private componentDetailsBusinessService: ComponentDetailsBusinessService,
              private form: ComponentDetailsFormState,
              private commands: ComponentDetailsCommandService) {
    super();
  }

  buildGridOptions(): GridOptions {
    return assign(
      {
        rowClassRules: {
          [ROW_DELETED_CLASS]: ({ data }: { data: any }) => data.active !== true,
          [ROW_DIRTY_CLASS]: ({ data }: { data: any }) => data.isDirty,
        },
      },
      createGridOptions(this.model));
  }

  buildColumnDefs(): Hash<ColDef> {
    const editable = this.form.editable && !this.form.locked;

    return {
      'cost': buildNumberColumn(NumberTypes.Price),
      'extension': buildNumberColumn(NumberTypes.Price),
      'isActive': { field: 'active', cellRenderer: falsyCellRenderer(rendererIconByName('done')) },
      'costCenter': editable
        ? {
          ...buildEditableAutocompleteColumn(
            {
              nameProperty: 'name',
              displayProperty: 'display',
              loadMore: (event) => event.load(this.componentDetailsBusinessService.loadCostCenters(event.query)),
            },
            this.model.getCellAccessor('costCenter')),
          comparator: gridEditableValueCollectionComparator(valueComparator('name')),
        }
        : {
          valueFormatter: autocompleteValueFormatter,
          comparator: gridValueCollectionComparator(valueComparator('name')),
        },
      quantity: editable
        ? buildEditableNumberColumn(NumberTypes.SignedQuantity, this.model.getCellAccessor('quantity'))
        : buildNumberColumn(NumberTypes.SignedQuantity),
    };
  }
}
